import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { Chart, Plugin } from "chart.js";

import { SemaforoBsc } from "src/app/shared/model/bsc/bsc.model";
import { pct } from "../bsc-formato";
import { COLOR_BSC, GraficaBsc, colorSemaforo } from "./grafica-bsc";

/** Una barra: el área con su alcance y su programado al corte (fracciones 0..1). */
export interface BarraAreaBsc {
  nombre: string;
  /** A_q del corte (snapshot o en vivo); nulo = sin dato. */
  real: number | null;
  /** AP_q del corte. */
  programado: number | null;
  semaforo?: SemaforoBsc | null;
}

/** Alto de cada renglón y lo que ocupan los ejes, en px. */
const ALTO_RENGLON = 34;
const ALTO_EJES = 44;
/** Espacio a la derecha para la columna del valor ("49.6%"). */
const COLUMNA_VALOR = 52;

/**
 * BSC · barras horizontales del alcance acumulado por área (A1, docs/BSC.md
 * §7 gráfica 1, como «CUMPLIMIENTO POR AREA» del FOR-011): la barra es el
 * alcance real al corte en el color de su semáforo; la raya vertical, lo
 * programado; la línea punteada que cruza, el objetivo Solser. El valor va
 * en una columna fija a la derecha, como en la maqueta.
 *
 * Siempre va junto a la tabla de áreas con los mismos números: la gráfica
 * no es la única forma de leerlos.
 */
@Component({
  selector: "app-barras-area-bsc",
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="bsc-barras" [style.height.px]="alto">
      <canvas #lienzo role="img" [attr.aria-label]="textoAccesible"></canvas>
    </div>
    <ul class="bsc-grafica-leyenda">
      <li><i class="bsc-grafica-leyenda__barra is-verde"></i><i class="bsc-grafica-leyenda__barra is-amarillo"></i
        ><i class="bsc-grafica-leyenda__barra is-rojo"></i>Real (color del semáforo)</li>
      <li><i class="bsc-grafica-leyenda__raya"></i>Programado</li>
      <li *ngIf="objetivo != null"><i class="bsc-grafica-leyenda__objetivo"></i>Objetivo Solser {{ textoObjetivo }}</li>
    </ul>
  `,
  styleUrls: ["./grafica-bsc.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarrasAreaBscComponent extends GraficaBsc {
  @Input() barras: BarraAreaBsc[] = [];
  /** Umbral gerencial del corte (fracción); nulo = sin línea. */
  @Input() objetivo: number | null = null;
  @Input() descripcion = "Alcance acumulado por área";

  get alto(): number {
    return Math.max(1, this.barras.length) * ALTO_RENGLON + ALTO_EJES;
  }

  get textoObjetivo(): string {
    return pct(this.objetivo);
  }

  get textoAccesible(): string {
    const detalle = this.barras
      .map((b) => `${b.nombre}: real ${pct(b.real)}, programado ${pct(b.programado)}`)
      .join("; ");
    const objetivo = this.objetivo != null ? `; objetivo Solser ${pct(this.objetivo)}` : "";
    return `${this.descripcion}. ${detalle}${objetivo}`;
  }

  protected arma(lienzo: HTMLCanvasElement): Chart | null {
    if (this.barras.length === 0) {
      return null;
    }
    const tinta = this.color(COLOR_BSC.tinta);
    const suave = this.color(COLOR_BSC.suave);
    const rejilla = this.color(COLOR_BSC.rejilla);
    const colorObjetivo = this.color(COLOR_BSC.objetivo);
    const objetivo = this.objetivo;
    const reales = this.barras.map((b) => b.real);
    const maximo = Math.max(1, ...this.barras.map((b) => Math.max(b.real ?? 0, b.programado ?? 0)));

    // La línea del objetivo y la columna de valores se dibujan a mano sobre el lienzo.
    const encima: Plugin<"bar" | "line"> = {
      id: "bscBarrasEncima",
      afterDatasetsDraw: (chart) => {
        const { ctx, chartArea } = chart;
        ctx.save();
        if (objetivo != null) {
          const x = chart.scales["x"].getPixelForValue(objetivo);
          ctx.strokeStyle = colorObjetivo;
          ctx.lineWidth = 1.5;
          ctx.setLineDash([4, 3]);
          ctx.beginPath();
          ctx.moveTo(x, chartArea.top);
          ctx.lineTo(x, chartArea.bottom);
          ctx.stroke();
          ctx.setLineDash([]);
        }
        ctx.fillStyle = tinta;
        ctx.font = "600 12px Roboto, system-ui, sans-serif";
        ctx.textAlign = "right";
        ctx.textBaseline = "middle";
        chart.getDatasetMeta(0).data.forEach((barra, i) => {
          ctx.fillText(pct(reales[i]), chartArea.right + COLUMNA_VALOR - 4, barra.y);
        });
        ctx.restore();
      },
    };

    return new Chart<"bar" | "line", (number | null)[], string>(lienzo, {
      type: "bar",
      data: {
        labels: this.barras.map((b) => b.nombre),
        datasets: [
          {
            type: "bar",
            label: "Real",
            data: reales,
            backgroundColor: this.barras.map((b) => this.color(colorSemaforo(b.semaforo))),
            borderRadius: 6,
            borderSkipped: false,
            barThickness: 14,
          },
          {
            type: "line",
            label: "Programado",
            data: this.barras.map((b) => b.programado),
            indexAxis: "y",
            showLine: false,
            pointStyle: "line",
            pointRotation: 90,
            pointRadius: 10,
            pointHoverRadius: 11,
            pointBorderWidth: 2,
            pointHoverBorderWidth: 3,
            borderColor: this.color(COLOR_BSC.programado),
            backgroundColor: this.color(COLOR_BSC.programado),
          },
        ],
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        layout: { padding: { right: COLUMNA_VALOR } },
        scales: {
          x: {
            min: 0,
            max: maximo,
            ticks: { color: suave, font: { size: 11 }, callback: (v) => pct(Number(v)), maxTicksLimit: 6 },
            grid: { color: rejilla },
            border: { display: false },
          },
          y: {
            ticks: { color: tinta, font: { size: 12 } },
            grid: { display: false },
            border: { display: false },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (c) => `${c.dataset.label}: ${pct(c.raw as number | null)}`,
            },
          },
        },
      },
      plugins: [encima],
    });
  }
}
